import { createWithEqualityFn } from 'zustand/traditional';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';

import type { VisibilityChange } from '~/domains/common/hooks/useVisibilityObserver';

type MarketVisibilityState = {
  visibleMarketGroupIds: Set<string>;
};

type MarketVisibilityActions = {
  setMarketGroupVisibility: (changes: VisibilityChange[]) => void;
  reset: () => void;
};

const initialState: MarketVisibilityState = {
  visibleMarketGroupIds: new Set(),
};

export const useMarketVisibilityStore = createWithEqualityFn<
  MarketVisibilityState & MarketVisibilityActions
>()(
  devtools(
    (set) => ({
      ...initialState,

      setMarketGroupVisibility: (changes) =>
        set(
          (state) => {
            const visibleMarketGroupIds = new Set(state.visibleMarketGroupIds);

            const hasChanged = changes.reduce((changed, { id, isVisible }) => {
              if (visibleMarketGroupIds.has(id) === isVisible) return changed;

              if (isVisible) {
                visibleMarketGroupIds.add(id);
              } else {
                visibleMarketGroupIds.delete(id);
              }

              return true;
            }, false);

            return hasChanged ? { visibleMarketGroupIds } : state;
          },
          false,
          'setMarketGroupVisibility'
        ),

      reset: () => set(initialState, false, 'reset'),
    }),
    { name: 'MarketVisibilityStore' }
  ),
  shallow
);

export function useVisibleMarketGroupIds(): Set<string> {
  return useMarketVisibilityStore((state) => state.visibleMarketGroupIds);
}

export function useSetMarketGroupVisibility(): (changes: VisibilityChange[]) => void {
  return useMarketVisibilityStore((state) => state.setMarketGroupVisibility);
}
